import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import ProgressBarBackground from './ProgressBarBackground';
import { useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

const selectBarStyles = (_ref, percentage) => {
  let {
    backgroundColor,
    borderRadius,
    outlineWidth,
    outlineColor
  } = _ref;
  return {
    backgroundColor,
    borderRadius,
    outlineWidth,
    outlineColor,
    width: `${percentage}%`
  };
};
/**
 * The `ProgressBar` is a visual representation of linear progression.
 * It provides simple but important information at a quick glance.
 *
 * ## Props
 *
 * - Use `percentage` prop to indicate completion
 *
 * ## Tokens
 *
 * - Use the following tokens to customize the appearance:
 *   - `backgroundColor` for the background color of the progress bar,
 *   - `borderRadius` for the rounded corners,
 *   - `outlineColor` to control the color of the border (outline),
 *   - `outlineWidth` to change the outline width.
 *
 * ## Variants
 *
 * - Use the following variants to render specific types of progress bars:
 *   - `negative`: set to `true` if you wish to use the negative theming for progress bar filling,
 *   - `inactive`: set to `true` if you wish to style the progress bar as inactive.
 *
 * ## Usability and A11y guidelines
 *
 * `ProgressBar` component accepts all the standard accessibility properties including the ones that
 * are specific to the `progressbar` role. By default the following props are used:
 *
 * - `accessibilityRole` (`role`): `progressbar`,
 * - `accessibilityValue.min` (`aria-valuemin`): `0`,
 * - `accessibilityValue.max` (`aria-valuemax`): `100`,
 * - `accessibilityValue.now` (`aria-valuenow`): `percentage`,
 * - `accessibilityValue.text` (`aria-valuetext`): `%{percentage}%`,
 *
 */


const ProgressBar = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    children = null,
    percentage = 0,
    tokens,
    variant,
    ...rest
  } = _ref2;
  const themeTokens = useThemeTokens('ProgressBar', tokens, variant);

  if (percentage < 0) {
    throw new Error('ProgressBar: `percentage` must be a positive number');
  } else if (percentage > 100) {
    throw new Error('ProgressBar: `percentage` cannot exceed 100');
  }

  const selectedProps = selectProps({
    accessibilityRole: 'progressbar',
    accessibilityValue: {
      min: 0,
      max: 100,
      now: percentage,
      text: `${percentage}%`
    },
    ...rest
  });
  return percentage > 0 ? /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: [staticStyles.bar, selectBarStyles(themeTokens, percentage)],
    ...selectedProps,
    children: children ?? /*#__PURE__*/_jsx(ProgressBarBackground, {
      variant: variant
    })
  }) : null;
});
ProgressBar.displayName = 'ProgressBar';
ProgressBar.propTypes = { ...selectedSystemPropTypes,

  /**
   * Optional children that can be used to customize the progress bar filling.
   */
  children: PropTypes.node,

  /**
   * Accessibility label for the progress bar.
   */
  accessibilityLabel: PropTypes.string.isRequired,

  /**
   * Percentage of completion.
   */
  percentage: PropTypes.number,

  /**
   * Progress bar tokens.
   */
  tokens: getTokensPropType('ProgressBar'),

  /**
   * Progress bar variant.
   */
  variant: variantProp.propType
};
export default ProgressBar;
const staticStyles = StyleSheet.create({
  bar: {
    height: '100%',
    overflow: 'hidden',
    position: 'absolute',
    ...Platform.select({
      web: {
        outlineStyle: 'solid'
      }
    })
  }
});